import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { AppService } from '../../../../app.service';
import { GlobalService } from '../../../../global/services/global.service';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { generalConfigFromGlobal } from '../../../../shared/workspace-element-context';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { PushCbcDataToEfileService } from './push-cbc-data-to-efile.service';

@Component({
  selector: 'gtw-push-cbc-data-to-efile',
  templateUrl: './push-cbc-data-to-efile.component.html',
  styleUrls: ['./push-cbc-data-to-efile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PushCbcDataToEfileComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  confirmConfig: ConfirmationModalConfig = {
    title: 'Confirm Push',
    message: 'Are you sure you want to push the data to EFile?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...',
  };

  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};

  constructor(
    private pushCbcDataToEfileService: PushCbcDataToEfileService,
    private gtwSnackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  onConfirm(): void {
    this.gtwSnackbarService.openSnackBar(
      'Data push to Efile has been initiated.',
      'gtw-snackbar--green'
    );
    this.dispatchUpdate('cancel');

    this.pushCbcDataToEfileService.pushDataToEfile(this.baseUrl, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        if (response?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar(
            'Data is pushed to Efile SuccessfullyBefore viewing the data in Efile, please contact E-File team (US_IST-Efile@pwc.com) to sync and validate the data.',
            'gtw-snackbar--green'
          );
        } else if (response?.errorMessage) {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        }
      },
      error: (error) => {
        this.gtwSnackbarService.openSnackBar(
          error?.message || 'Something went wrong while completing the data push, please try again!',
          'gtw-snackbar--red'
        );
      },
    });
  }

  onCancel(): void {
    this.dispatchUpdate('cancel');
  }

  private dispatchUpdate(detail: 'refresh' | 'cancel'): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail,
      bubbles: true,
    });
    document.querySelector('gtw-push-cbc-data-to-efile-ce')?.dispatchEvent(updateEvent);
  }
}
